/**
 * EE AI Provider 管理頁（Phase 5 + Phase 7 多租戶）
 *
 * 同一個元件，依 URL path 自適應兩個視角：
 *   - /ee_ai_providers          → 平台管理員（mana）視角，看全部、可 share、可 toggle share_all_tenants
 *   - /ee_ai_providers_tenant   → 租戶視角，看自家 own + 共享來的、不能 share、共享來的只能 disable
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Stack, Button, IconButton, Tooltip, Chip, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Switch, Typography, Alert, CircularProgress,
  Divider, List, ListItem, ListItemButton, ListItemText, ListItemIcon,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Edit as EditIcon,
  Delete as DeleteIcon, Star as StarIcon, StarBorder as StarBorderIcon,
  WifiTethering as TestIcon, SmartToy as AiIcon,
  Public as PublicIcon, Share as ShareIcon, Lock as LockIcon,
} from '@mui/icons-material';

import FunctionPageHeader from '../../components/FunctionPageHeader';
import { useMessage } from '../../contexts/MessageContext';
import { logView, logCreate, logUpdate, logDelete } from '../../utils/userLogHelper';
import { usePermission } from '../../hooks/usePermission';
import {
  AiProvider, AiProviderCreate, AiProviderUpdate, AiProviderTestResponse,
  listAiProviders, createAiProvider, updateAiProvider,
  deleteAiProvider, testAiProvider, setDefaultAiProvider,
  setProviderTenantActive, setProviderShareAllTenants,
  listProviderOrgs, shareProviderToOrg, revokeProviderShare,
  ProviderOrgLink,
} from '../../services/eeAiService';
import { Organization, getOrganizations } from '../../services/organizationService';
import { formatLocal } from '../../utils/dateHelper';

// log 一律記在平台 func_code 之下；UI 視角差異由 useLocation 判斷
const FUNC_CODE = 'ee_ai_providers';

const PROVIDER_TYPES = [
  { value: 'ollama',    label: 'Ollama（本地）' },
  { value: 'openai',   label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
];

const emptyForm = (): AiProviderCreate => ({
  provider_code: '',
  provider_name: '',
  provider_type: 'ollama',
  base_url: 'http://10.1.0.225:11434',
  api_key: '',
  default_model: '',
  request_timeout_sec: 60,
  is_active: true,
  is_default: false,
  key_source: 'platform',
  provided_by_org_id: null,
  share_allowed: false,
});

const fmt = (iso?: string | null) =>
  iso ? formatLocal(iso) : '—';

const TestStatusChip: React.FC<{ status?: string | null }> = ({ status }) => {
  if (!status) return <Chip label="未測試" size="small" />;
  return (
    <Chip
      label={status === 'success' ? '連線正常' : '連線失敗'}
      size="small"
      color={status === 'success' ? 'success' : 'error'}
    />
  );
};

// ============================================================
// 連線測試結果 Dialog
// ============================================================
interface TestResultDialogProps {
  open: boolean;
  result: AiProviderTestResponse | null;
  onClose: () => void;
}
const TestResultDialog: React.FC<TestResultDialogProps> = ({ open, result, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>連線測試結果</DialogTitle>
    <DialogContent>
      {result && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity={result.success ? 'success' : 'error'}>
            {result.message}
          </Alert>
          {result.success && result.models.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                可用模型（{result.models.length} 個）
              </Typography>
              <List dense disablePadding>
                {result.models.map(m => (
                  <ListItem key={m} disableGutters sx={{ py: 0.25 }}>
                    <ListItemText primary={m} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Stack>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>關閉</Button>
    </DialogActions>
  </Dialog>
);

// ============================================================
// 新增 / 編輯 Dialog
// ============================================================
interface EditDialogProps {
  open: boolean;
  editing: AiProvider | null;
  form: AiProviderCreate;
  onChange: (f: AiProviderCreate) => void;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
}
const EditDialog: React.FC<EditDialogProps> = ({
  open, editing, form, onChange, onSave, onClose, saving,
}) => {
  const needsKey = form.provider_type !== 'ollama';
  const needsBaseUrl = form.provider_type !== 'anthropic';   // anthropic 留空 = 官方 endpoint
  const valid = form.provider_code && form.provider_name && form.default_model
                && (!needsBaseUrl || form.base_url);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? '編輯 AI Provider' : '新增 AI Provider'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Provider 代碼"
            size="small" fullWidth required
            value={form.provider_code}
            onChange={e => onChange({ ...form, provider_code: e.target.value })}
            disabled={!!editing}
            helperText={editing ? '建立後不可修改' : '程式識別碼，如 ollama_local'}
          />
          <TextField
            label="顯示名稱"
            size="small" fullWidth required
            value={form.provider_name}
            onChange={e => onChange({ ...form, provider_name: e.target.value })}
          />
          <FormControl size="small" fullWidth required>
            <InputLabel>類型</InputLabel>
            <Select
              label="類型"
              value={form.provider_type}
              onChange={e => onChange({
                ...form,
                provider_type: e.target.value as AiProviderCreate['provider_type'],
                base_url: e.target.value === 'ollama' ? 'http://10.1.0.225:11434' : '',
              })}
            >
              {PROVIDER_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Endpoint URL"
            size="small" fullWidth
            required={form.provider_type !== 'anthropic'}
            value={form.base_url}
            onChange={e => onChange({ ...form, base_url: e.target.value })}
            placeholder={
              form.provider_type === 'ollama' ? 'http://10.1.0.225:11434'
              : form.provider_type === 'anthropic' ? '（留空走官方 https://api.anthropic.com，corp proxy 才填）'
              : 'https://api.openai.com/v1'
            }
            helperText={form.provider_type === 'anthropic' ? '空 = 官方 endpoint' : undefined}
          />
          {needsKey && (
            <TextField
              label="API Key"
              size="small" fullWidth
              type="password"
              value={form.api_key || ''}
              onChange={e => onChange({ ...form, api_key: e.target.value })}
              helperText={editing ? "留空不更新；輸入 '***' 以外的值才會更新" : ''}
            />
          )}
          <TextField
            label="預設模型"
            size="small" fullWidth required
            value={form.default_model}
            onChange={e => onChange({ ...form, default_model: e.target.value })}
            placeholder={
              form.provider_type === 'ollama' ? 'qwen2.5:7b'
              : form.provider_type === 'anthropic' ? 'claude-sonnet-4-6'
              : 'gpt-4o'
            }
          />
          <TextField
            label="逾時秒數"
            size="small" type="number"
            inputProps={{ min: 5, max: 300 }}
            value={form.request_timeout_sec}
            onChange={e => onChange({ ...form, request_timeout_sec: Number(e.target.value) })}
          />
          <Divider />
          {/* 4-pre-c · key 來源 + BYO 分潤 */}
          <FormControl size="small" fullWidth>
            <InputLabel>Key 來源</InputLabel>
            <Select label="Key 來源" value={form.key_source}
              onChange={e => {
                const ks = e.target.value as 'platform' | 'tenant_byo';
                onChange({ ...form, key_source: ks,
                  provided_by_org_id: ks === 'platform' ? null : form.provided_by_org_id,
                  share_allowed: ks === 'platform' ? false : form.share_allowed });
              }}>
              <MenuItem value="platform">平台代管（porsche 提供）</MenuItem>
              <MenuItem value="tenant_byo">租戶 BYO（客戶自帶）</MenuItem>
            </Select>
          </FormControl>
          {form.key_source === 'tenant_byo' && (
            <>
              <TextField label="提供者租戶 org id" size="small" type="number" fullWidth required
                value={form.provided_by_org_id ?? ''}
                onChange={e => onChange({ ...form, provided_by_org_id: e.target.value ? Number(e.target.value) : null })}
                helperText="此 BYO key 的擁有租戶（分潤對象）" />
              <FormControlLabel
                control={<Switch checked={!!form.share_allowed}
                  onChange={e => onChange({ ...form, share_allowed: e.target.checked })} />}
                label="允許共享給其他租戶（分潤 · 請先取得客戶書面同意）" />
            </>
          )}
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={e => onChange({ ...form, is_active: e.target.checked })}
                />
              }
              label="啟用"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_default}
                  onChange={e => onChange({ ...form, is_default: e.target.checked })}
                />
              }
              label="設為預設"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>取消</Button>
        <Button
          variant="contained" onClick={onSave}
          disabled={!valid || saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {editing ? '儲存' : '建立'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================
// 主頁面
// ============================================================
const EeAiProvidersPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  // 判斷視角：URL 是 ee_ai_providers_tenant → 租戶視角；否則平台管理員視角
  const isPlatformView = !location.pathname.includes('ee_ai_providers_tenant');

  const { showSuccess, showApiError } = useMessage();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canUpdate = hasPermission(FUNC_CODE, 'update');
  const canDelete = hasPermission(FUNC_CODE, 'delete');

  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);

  // 共享 dialog 狀態（平台管理員專）
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState<AiProvider | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<AiProvider | null>(null);
  const [form, setForm] = useState<AiProviderCreate>(emptyForm());

  const [testOpen, setTestOpen] = useState(false);
  const [testResult, setTestResult] = useState<AiProviderTestResponse | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAiProviders();
      setProviders(data);
      logView(FUNC_CODE, { count: data.length });
    } catch (err: any) {
      logView(FUNC_CODE, {}, String(err?.message || err));
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setEditOpen(true);
  };

  const openEdit = (p: AiProvider) => {
    setEditing(p);
    setForm({
      provider_code: p.provider_code,
      provider_name: p.provider_name,
      provider_type: p.provider_type,
      base_url: p.base_url,
      api_key: p.has_api_key ? '***' : '',
      default_model: p.default_model,
      request_timeout_sec: p.request_timeout_sec,
      is_active: p.is_active,
      is_default: p.is_default,
      key_source: p.key_source || 'platform',
      provided_by_org_id: p.provided_by_org_id ?? null,
      share_allowed: !!p.share_allowed,
    });
    setEditOpen(true);
  };

  // 安全 sanitizer：永不把 api_key 寫進 user_logs
  const sanitize = (data: any) => {
    if (!data || typeof data !== 'object') return data;
    const { api_key, ...rest } = data;
    return { ...rest, has_api_key: api_key ? '(set)' : '(empty)' };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload: AiProviderUpdate = {
          provider_name: form.provider_name,
          provider_type: form.provider_type,
          base_url: form.base_url,
          default_model: form.default_model,
          request_timeout_sec: form.request_timeout_sec,
          is_active: form.is_active,
          is_default: form.is_default,
          key_source: form.key_source,
          provided_by_org_id: form.provided_by_org_id,
          share_allowed: form.share_allowed,
          api_key: form.api_key || undefined,
        };
        await updateAiProvider(editing.id, payload);
        logUpdate(FUNC_CODE, sanitize({ ...editing }), sanitize({ ...payload, id: editing.id }));
        showSuccess('SYS050002', { name: form.provider_name, id: String(editing.id) });
      } else {
        const created = await createAiProvider(form);
        logCreate(FUNC_CODE, sanitize({ ...created }));
        showSuccess('SYS050001', {
          name: form.provider_name,
          provider_code: form.provider_code,
          type: form.provider_type,
        });
      }
      setEditOpen(false);
      reload();
    } catch (err: any) {
      if (editing) {
        logUpdate(FUNC_CODE, sanitize({ ...editing }), sanitize(form), String(err?.message || err));
      } else {
        logCreate(FUNC_CODE, sanitize(form), String(err?.message || err));
      }
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p: AiProvider) => {
    if (!window.confirm(`確定刪除 Provider「${p.provider_name}」？`)) return;
    try {
      await deleteAiProvider(p.id);
      logDelete(FUNC_CODE, sanitize({ ...p }));
      showSuccess('SYS050003', { name: p.provider_name, id: String(p.id) });
      reload();
    } catch (err: any) {
      logDelete(FUNC_CODE, sanitize({ ...p }), String(err?.message || err));
      showApiError(err);
    }
  };

  const handleTest = async (p: AiProvider) => {
    setTesting(p.id);
    const intent = { action: 'test_provider', provider_id: p.id, provider_code: p.provider_code };
    try {
      const result = await testAiProvider(p.id);
      logUpdate(FUNC_CODE, intent,
        { ...intent, success: result.success, models_found: result.models?.length });
      setTestResult(result);
      setTestOpen(true);
      reload();
    } catch (err: any) {
      logUpdate(FUNC_CODE, intent, intent, String(err?.message || err));
      showApiError(err);
      reload();
    } finally {
      setTesting(null);
    }
  };

  const handleSetDefault = async (p: AiProvider) => {
    const intent = { action: 'set_default', provider_id: p.id, provider_code: p.provider_code };
    try {
      await setDefaultAiProvider(p.id);
      logUpdate(FUNC_CODE,
        { ...intent, was_default: p.is_default },
        { ...intent, is_default: true });
      showSuccess('SYS050005', { id: String(p.id), provider_code: p.provider_code });
      reload();
    } catch (err: any) {
      logUpdate(FUNC_CODE, intent, intent, String(err?.message || err));
      showApiError(err);
    }
  };

  // Phase 7：租戶 flip is_active_for_tenant（共享來的也能 disable，不能刪）
  const handleTenantToggleActive = async (p: AiProvider) => {
    const next = !p.is_active_for_tenant;
    try {
      await setProviderTenantActive(p.id, next);
      logUpdate(FUNC_CODE,
        { action: 'toggle_tenant_active', provider_id: p.id, was: p.is_active_for_tenant },
        { action: 'toggle_tenant_active', provider_id: p.id, now: next });
      showSuccess('SYS020002', {
        name: p.provider_name,
        id: String(p.id),
      });
      reload();
    } catch (err: any) {
      showApiError(err);
    }
  };

  // 平台管理員勾共享給所有租戶
  const handleToggleShareAll = async (p: AiProvider) => {
    const next = !p.share_all_tenants;
    try {
      await setProviderShareAllTenants(p.id, next);
      logUpdate(FUNC_CODE,
        { action: 'toggle_share_all', provider_id: p.id, was: p.share_all_tenants },
        { action: 'toggle_share_all', provider_id: p.id, now: next });
      // 用系統訊息（MessageContext / Snackbar）回饋；操作可逆 — 再點一次就能取消
      showSuccess('SYS020002', {
        name: `${p.provider_name}${next ? ' 已設為平台共享（所有租戶可用）'
                                         : ' 已取消平台共享'}`,
        id: String(p.id),
      });
      reload();
    } catch (err: any) {
      showApiError(err);
    }
  };

  const openShareDialog = (p: AiProvider) => {
    setShareTarget(p);
    setShareDialogOpen(true);
  };

  return (
    <Box>
      <FunctionPageHeader funcCode="ee_ai_providers" fallback="AI Provider 管理" />

      <Stack direction="row" spacing={1} sx={{ my: 2 }} alignItems="center">
        {canCreate && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
            新增 Provider
          </Button>
        )}
        <Button
          variant="outlined" size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={reload} disabled={loading}
        >
          重新整理
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          共 {providers.length} 個 Provider
        </Typography>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell width={50}>ID</TableCell>
              <TableCell>名稱</TableCell>
              <TableCell width={100}>類型</TableCell>
              <TableCell>Endpoint</TableCell>
              <TableCell width={110}>預設模型</TableCell>
              <TableCell width={90}>連線狀態</TableCell>
              <TableCell width={70}>啟用</TableCell>
              <TableCell width={50}>預設</TableCell>
              <TableCell width={150} align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {providers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  <Stack alignItems="center" spacing={1}>
                    <AiIcon sx={{ fontSize: 36, color: 'action.disabled' }} />
                    <Typography variant="body2">
                      {loading ? '載入中…' : '尚無 AI Provider，請點擊「新增 Provider」'}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              providers.map(p => (
                <TableRow key={p.id} hover
                  sx={p.is_default ? { bgcolor: 'primary.50' } : undefined}>
                  <TableCell>{p.id}</TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={p.is_default ? 600 : 400}>
                        {p.provider_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.provider_code}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={p.provider_type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {p.base_url}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {p.default_model}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={p.last_test_at ? `最近測試：${fmt(p.last_test_at)}` : '尚未測試'}>
                      <span>
                        <TestStatusChip status={p.last_test_status} />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={p.is_active ? '啟用' : '停用'}
                      size="small"
                      color={p.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {p.is_default && (
                      <Tooltip title="系統預設 Provider">
                        <StarIcon fontSize="small" color="warning" />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      {/* 來源 chip */}
                      {!p.is_owner && p.via_share_all && (
                        <Tooltip title="平台管理員設定為基本盤（share_all_tenants）">
                          <Chip size="small" icon={<PublicIcon sx={{ fontSize: 12 }} />}
                                label="平台共享" variant="outlined" color="info" />
                        </Tooltip>
                      )}
                      {!p.is_owner && !p.via_share_all && (
                        <Tooltip title="平台管理員手動共享給本租戶">
                          <Chip size="small" icon={<ShareIcon sx={{ fontSize: 12 }} />}
                                label="共享" variant="outlined" color="info" />
                        </Tooltip>
                      )}
                      {/* share_all_tenants 旗標（mana 才看得到） */}
                      {isPlatformView && p.is_owner && p.share_all_tenants && (
                        <Tooltip title="此 Provider 共享給所有租戶">
                          <Chip size="small" icon={<PublicIcon sx={{ fontSize: 12 }} />}
                                label="全租戶" color="success" variant="filled" />
                        </Tooltip>
                      )}

                      {canUpdate && (
                        <Tooltip title="連線測試">
                          <span>
                            <IconButton size="small"
                              onClick={() => handleTest(p)}
                              disabled={testing === p.id}>
                              {testing === p.id ? <CircularProgress size={16} />
                                : <TestIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      {/* 共享來的：只有 disable / enable，不能 share / 刪 / 編輯 */}
                      {!p.is_owner && (
                        <Tooltip title={p.is_active_for_tenant ? '在自家視角停用' : '在自家視角啟用'}>
                          <IconButton size="small" onClick={() => handleTenantToggleActive(p)}>
                            {p.is_active_for_tenant
                              ? <LockIcon fontSize="small" color="action" />
                              : <LockIcon fontSize="small" color="warning" />}
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* owner / 平台管理員：完整操作 */}
                      {p.is_owner && canUpdate && !p.is_default && (
                        <Tooltip title="設為預設">
                          <IconButton size="small" onClick={() => handleSetDefault(p)}>
                            <StarBorderIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {p.is_owner && canUpdate && (
                        <Tooltip title="編輯">
                          <IconButton size="small" onClick={() => openEdit(p)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* 平台管理員：share_all_tenants 旗標 toggle */}
                      {isPlatformView && p.is_owner && (
                        <Tooltip title={p.share_all_tenants
                          ? '取消平台共享（基本盤）'
                          : '設為平台共享（所有租戶可用）'}>
                          <IconButton size="small" onClick={() => handleToggleShareAll(p)}>
                            <PublicIcon fontSize="small"
                              color={p.share_all_tenants ? 'success' : 'action'} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* 平台管理員：手動共享給特定租戶 */}
                      {isPlatformView && p.is_owner && (
                        <Tooltip title="共享給特定租戶...">
                          <IconButton size="small" onClick={() => openShareDialog(p)}>
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {p.is_owner && canDelete && (
                        <Tooltip title={p.is_default ? '預設 Provider 不可刪除' : '刪除'}>
                          <span>
                            <IconButton size="small"
                              onClick={() => handleDelete(p)}
                              disabled={p.is_default}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <EditDialog
        open={editOpen}
        editing={editing}
        form={form}
        onChange={setForm}
        onSave={handleSave}
        onClose={() => setEditOpen(false)}
        saving={saving}
      />

      <TestResultDialog
        open={testOpen}
        result={testResult}
        onClose={() => setTestOpen(false)}
      />

      {/* Phase 7：共享給特定租戶 dialog（mana 專） */}
      <ShareDialog
        open={shareDialogOpen}
        provider={shareTarget}
        onClose={() => { setShareDialogOpen(false); setShareTarget(null); }}
        onChanged={reload}
      />
    </Box>
  );
};


// ============================================================
// 共享給特定租戶 Dialog（平台管理員專）
// ============================================================
interface ShareDialogProps {
  open: boolean;
  provider: AiProvider | null;
  onClose: () => void;
  onChanged: () => void;
}

const ShareDialog: React.FC<ShareDialogProps> = ({ open, provider, onClose, onChanged }) => {
  const { showSuccess, showApiError } = useMessage();
  const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
  const [linked, setLinked] = useState<ProviderOrgLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyOrgId, setBusyOrgId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const reload = useCallback(async () => {
    if (!provider) return;
    setLoading(true);
    try {
      const [orgsAll, linksAll] = await Promise.all([
        getOrganizations({ is_active: true, limit: 1000 }),
        listProviderOrgs(provider.id),
      ]);
      setAllOrgs(orgsAll);
      setLinked(linksAll);
    } catch (err: any) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [provider, showApiError]);

  useEffect(() => {
    if (open && provider) reload();
  }, [open, provider, reload]);

  const linkByOrgId = new Map(linked.map(l => [l.organization_id, l]));

  const handleToggle = async (org: Organization) => {
    if (!provider) return;
    const link = linkByOrgId.get(org.id);
    if (link?.is_owner) return;     // owner 不能切

    setBusyOrgId(org.id);
    try {
      if (link) {
        // 已 link → 撤銷
        await revokeProviderShare(provider.id, org.id);
        showSuccess('SYS020003', { name: `撤銷 ${org.org_name}`, id: String(org.id) });
      } else {
        // 未 link → 共享
        await shareProviderToOrg(provider.id, org.id);
        showSuccess('SYS020001', { name: `共享給 ${org.org_name}` });
      }
      reload();
      onChanged();
    } catch (err: any) {
      showApiError(err);
    } finally {
      setBusyOrgId(null);
    }
  };

  const filteredOrgs = allOrgs.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return o.org_name.toLowerCase().includes(q)
        || o.org_code.toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        共享 Provider — {provider?.provider_name}
        <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}>
          勾選 = 共享給該租戶；取消勾 = 撤銷共享。OWNER 不能切。
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          ⚠️ 共享 FT 過的模型可能違反 Anthropic ToS — 建議只共享 base 模型。
        </Alert>

        <TextField
          fullWidth size="small"
          placeholder="🔍 搜尋組織名稱 / 代碼..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {filteredOrgs.length === 0 ? (
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', textAlign: 'center', py: 3 }}>
                {allOrgs.length === 0 ? '尚無組織' : '搜尋無結果'}
              </Typography>
            ) : filteredOrgs.map((org) => {
              const link = linkByOrgId.get(org.id);
              const isOwner = !!link?.is_owner;
              const isShared = !!link;
              const checked = isShared;
              const isBusy = busyOrgId === org.id;
              return (
                <ListItem key={org.id} disablePadding
                  secondaryAction={
                    isOwner ? (
                      <Chip label="OWNER" size="small" color="primary" />
                    ) : isBusy ? (
                      <CircularProgress size={18} />
                    ) : null
                  }>
                  <ListItemButton
                    onClick={() => { if (!isOwner && !isBusy) handleToggle(org); }}
                    disabled={isOwner || isBusy}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox edge="start"
                        checked={checked}
                        disabled={isOwner || isBusy}
                        tabIndex={-1} disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{org.org_name}</Typography>
                          {org.is_mana && (
                            <Chip label="平台管理組織" size="small" color="warning"
                                  variant="outlined" sx={{ height: 18 }} />
                          )}
                        </Stack>
                      }
                      secondary={
                        <Stack direction="row" spacing={1}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {org.org_code} · #{org.id}
                          </Typography>
                          {link && !isOwner && !link.is_active_for_tenant && (
                            <Chip label="自家停用中" size="small" color="default"
                                  variant="outlined" sx={{ height: 16 }} />
                          )}
                        </Stack>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 1 }}>
          {linked.filter(l => !l.is_owner).length} 個租戶有共享 / 共 {allOrgs.length} 個
        </Typography>
        <Button onClick={onClose}>關閉</Button>
      </DialogActions>
    </Dialog>
  );
};


export default EeAiProvidersPage;